import React, { useCallback, useEffect, useState } from 'react';
import MarginToolCard from '../../Common/MarginToolCard';
import { MarginToolCardTitle } from '../../Common/MarginToolCardTitle';
import { CircularProgress, Grid, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ControlledTypeAhead } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { useFormContext } from 'react-hook-form';
import { MarginToolCheckbox } from '@AMIEWEB/MarginTool/Common/MarginToolCheckbox';
import { useDispatch, useSelector } from 'react-redux';
import { placementMarginAssignmentActions } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.redux';
import {
  selectInitialPlacementMarginScenarioMarginDetailsResponse,
  selectPlacementMarginInsuranceStatus,
} from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { BenefitsInsuranceStatus } from '@AMIEWEB/MarginTool/enum';
import { IPlacementMarginDetailsResponseScenarioSplitItem } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { selectSelectedPlacementMarginScenario } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { PlacementMarginPayPackageStatus } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { usePeopleSoftCalculation } from '../PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { useDebouncedTriggerCalculation, useInitialMarginDetails } from '@AMIEWEB/MarginTool/hooks';
import { findInitialSplitValueBySplitId } from '@AMIEWEB/MarginTool/helper';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { usePromiseTracker } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { getDefaultInsurance, IInsurance } from './helper';



export const Benefits = ({
  splitIndex,
  field,
  sortedAssignmentSplits = [],
}: {
  splitIndex: number;
  field: IPlacementMarginDetailsResponseScenarioSplitItem;
  sortedAssignmentSplits: IPlacementMarginDetailsResponseScenarioSplitItem[];
}) => {
  const theme = useTheme();
  const { control, setValue, register, watch } = useFormContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { triggerPeopleSoftCalculation } = usePeopleSoftCalculation();
  const selectedScenario = useSelector(selectSelectedPlacementMarginScenario);
  const insuranceStatusOptions = useSelector(selectPlacementMarginInsuranceStatus);
  const insurance = getDefaultInsurance(field?.healthInsuranceType);
  const selectedInsurance = watch(`assignmentSplits.${splitIndex}.insurance`);
  const initialScenarioResponse = useSelector(selectInitialPlacementMarginScenarioMarginDetailsResponse);
  const { initiateRefreshCall } = useInitialMarginDetails();
  const { debouncedTriggerPeopleSoftCalculation } = useDebouncedTriggerCalculation();
  const [isRefreshInProgress, setIsRefreshInProgress] = useState<boolean>(false);

  const { promiseInProgress: isGSACalculateFetchDataInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData,
    delay: 0,
  });

  const { promiseInProgress: isPeopleSoftCallMarginToolInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress,
    delay: 0,
  });

  useEffect(() => {
    dispatch(placementMarginAssignmentActions.getPlacementMarginPickListDetails());
  }, []);

  //Method to update the insurance status and waiting period of following splits
  const handleSplitBenefitChange = ({
    value,
    currSelectedInsurance,
    currSplitIndex,
  }: {
    value: boolean;
    currSelectedInsurance: IInsurance;
    currSplitIndex: number;
  }) => {
    if (currSelectedInsurance.object.id === BenefitsInsuranceStatus.acceptedId && sortedAssignmentSplits.length > 1) {
      sortedAssignmentSplits.forEach((_: IPlacementMarginDetailsResponseScenarioSplitItem, idx: number) => {
        if (idx > currSplitIndex) {
          dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(true));
          setValue(`assignmentSplits.${idx}.insurance`, currSelectedInsurance);
          setValue(`assignmentSplits.${idx}.waitingPeriod`, value);
          dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
        }
      });
    }
  };

  const handleInsuranceChange = e => {
    setValue(`assignmentSplits.${splitIndex}.insurance`, e);
    triggerPeopleSoftCalculation();
  };

  /**
   * Method to handle field updates after refresh button is clicked
   */

  const handleFieldUpdatesAfterRefresh = useCallback(
    (initialSplit: IPlacementMarginDetailsResponseScenarioSplitItem) => {
      const defaultInsurance = getDefaultInsurance(initialSplit?.healthInsuranceType);
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(true));
      setValue(`assignmentSplits.${splitIndex}.insurance`, defaultInsurance, {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.waitingPeriod`, initialSplit?.isWaitingPeriodBenefit, {
        shouldDirty: true,
      });
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
      debouncedTriggerPeopleSoftCalculation();
      setIsRefreshInProgress(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setValue, debouncedTriggerPeopleSoftCalculation, setIsRefreshInProgress],
  );

  /**
   * useEffect to handle field updates for benefits container after refresh button is clicked
   */
  useEffect(() => {
    if (isRefreshInProgress && initialScenarioResponse) {
      const initialSplit = findInitialSplitValueBySplitId(initialScenarioResponse, field?.splitId);
      if (initialSplit) {
        handleFieldUpdatesAfterRefresh(initialSplit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScenarioResponse, isRefreshInProgress, splitIndex]);

  return (
    <MarginToolCard
      id="benefits-card-marginToolDetailsPage"
      title={<MarginToolCardTitle title={t('marginTool.components.assignment.benefits')} />}
      customBackGroundColor={theme.palette.framework.system.white}
      showRefreshButton={!isRefreshInProgress}
      icon={<CircularProgress size={16} color="inherit" />}
      isCustomIcon={isRefreshInProgress}
      onRefresh={() => {
        initiateRefreshCall();
        setIsRefreshInProgress(true);
      }}
      children={
        <Grid container spacing={4} sx={{ p: 2 }}>
          <Grid item xs={4}>
            <ControlledTypeAhead
              name={`assignmentSplits.${splitIndex}.insurance`}
              label={t('marginTool.labels.insurance')}
              register={register}
              onChange={e => handleInsuranceChange(e)}
              defaultValue={insurance}
              disabled={
                selectedScenario?.scenarioStatusId !== PlacementMarginPayPackageStatus.Pending ||
                isGSACalculateFetchDataInProgress ||
                isPeopleSoftCallMarginToolInProgress
              }
              control={control}
              options={insuranceStatusOptions || []}
              customScroll={true}
              id={'marginTool-benefits-insuranceDropdown'}
              showDropdownIcon={true}
            ></ControlledTypeAhead>
          </Grid>
          <Grid item sx={{ alignContent: 'center' }}>
            <MarginToolCheckbox
              name={`assignmentSplits.${splitIndex}.waitingPeriod`}
              id="marginTool-benefits-waitingPeriodCheckbox"
              defaultChecked={field?.isWaitingPeriodBenefit}
              label={t('marginTool.labels.waitingPeriod')}
              disabled={
                !!(selectedInsurance?.object?.id === BenefitsInsuranceStatus.declinedId) ||
                selectedScenario?.scenarioStatusId !== PlacementMarginPayPackageStatus.Pending ||
                isGSACalculateFetchDataInProgress ||
                isPeopleSoftCallMarginToolInProgress
              }
              onHandleChange={(waitingPeriodValue: boolean) => {
                handleSplitBenefitChange({
                  value: waitingPeriodValue,
                  currSelectedInsurance: selectedInsurance,
                  currSplitIndex: splitIndex,
                });
                triggerPeopleSoftCalculation();
              }}
            />
          </Grid>
        </Grid>
      }
    />
  );
};
